import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import ReactFlow, {
  MiniMap,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Edge,
  Node,
  Connection,
  MarkerType,
} from 'reactflow';
import {
  OnboardingFlowTransition,
  OnboardingFlowNode,
  OnboardingFlowNodeType,
  OnboardingFlowTerminusNode,
  IOnboardingFlow,
  IOnboardingFlowDraft,
} from '@scale/shared/onboardingControlPanel';
import { getCampaignsForOCPFlow } from 'react-scale/corp/lib/api/growth';
import FlowNode from './FlowNode';
import FlowEdge from './FlowEdge';
import { FLOW_NODE_TYPE, FLOW_EDGE_TYPE, nodeId } from '../common';
import { useOCPStore } from '../hooks/useOCPStore';
import { useDraftFlowStore } from '../hooks/useDraftFlowStore';
import _ from 'lodash';
import dagre from 'dagre';
import { Switch } from '@scale/scaleui';
import InfoBanner from './InfoBanner';
import { useOCPTransitionNumbers } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { EmailDripCampaign } from '@scale/shared-types';

const dagreGraphLayout = (
  nodes: OnboardingFlowNode[],
  edges: OnboardingFlowTransition[],
  detailTarget: OnboardingFlowTransition,
  showFailureNodes: boolean,
  transitionData: Record<string, number>,
  emailCampaignsData: Record<string, EmailDripCampaign>,
) => {
  const nodeWidth = 330;
  const nodeHeight = 200;
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR' });
  const failureNodes: OnboardingFlowTerminusNode[] = [];
  nodes.forEach(node => {
    if (
      node.type === OnboardingFlowNodeType.Terminus &&
      !(node as OnboardingFlowTerminusNode).success
    )
      failureNodes.push(node);
    else dagreGraph.setNode(node.name, { width: nodeWidth, height: nodeHeight });
  });
  const failureEdges: OnboardingFlowTransition[] = [];
  edges.forEach(edge => {
    if (!edge.sourceNode || !edge.destNode) return;
    if (failureNodes.map(n => n.name).includes(edge.destNode)) failureEdges.push(edge);
    else dagreGraph.setEdge(edge.sourceNode, edge.destNode);
  });
  dagre.layout(dagreGraph);
  const flowNodes = nodes
    .filter(node => {
      if (showFailureNodes) return true;
      return !failureNodes.map(n => n.name).includes(node.name);
    })
    .map((n: OnboardingFlowNode) => {
      const node: any = { ...n };
      let nodeWithPosition = dagreGraph.node(node.name);
      let isFailureNode = false;
      if (failureNodes.map(n => n.name).includes(node.name)) {
        isFailureNode = true;
        const failureEdge = failureEdges.find(e => e.destNode === node.name);
        if (failureEdge) {
          const connectedNode = nodes.find(n => n.name === failureEdge?.sourceNode);
          if (connectedNode) {
            nodeWithPosition = dagreGraph.node(connectedNode.name);
          }
        }
      }
      if (!nodeWithPosition) {
        nodeWithPosition = node.position ?? { x: 0, y: 0 };
      }
      node.data = { ..._.omit(node, ['data']), type: node.type };
      if (node.name && emailCampaignsData[node.name]) {
        node.data.emailCampaignId = emailCampaignsData[node.name]._id;
      }
      node.targetPosition = 'left';
      node.sourcePosition = 'right';
      node.id = nodeId(node);
      node.type = FLOW_NODE_TYPE;
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2 + (isFailureNode ? 120 : 0),
      };
      return node;
    });

  const flowEdges = edges
    .filter(edge => {
      if (showFailureNodes) return true;
      return !failureEdges.map(e => e.name).includes(edge.name);
    })
    .map(transition => {
      const selected =
        (detailTarget as OnboardingFlowTransition)?.sourceNode === transition?.sourceNode &&
        (detailTarget as OnboardingFlowTransition)?.destNode === transition?.destNode;

      const transitionUserCount = transitionData
        ? transitionData[`${transition.sourceNode}:${transition.destNode}`] ?? 0
        : '—';

      const possibleTransitions = edges.filter(
        e => e.sourceNode === transition.sourceNode && e.destNode === transition.destNode,
      );

      return {
        data: {
          ..._.omit(transition, ['data']),
          label: {
            transitionUserCount,
            transitionNumber: possibleTransitions.length,
            transitionNames: possibleTransitions.map(possibleTransition => possibleTransition.name),
          },
          isFailureNodeTransition: failureEdges.map(e => e.name).includes(transition.name),
        },
        id: transition.name,
        source: nodeId(transition.sourceNode),
        target: nodeId(transition.destNode),
        type: FLOW_EDGE_TYPE,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 10,
          height: 10,
          color: selected ? '#6A5DF9' : '#6B7280',
        },
        style: {
          strokeWidth: 2,
        },
      };
    });

  return { flowNodes, flowEdges };
};

const minimapStyle = {
  height: 120,
};

const nodeTypes = { [FLOW_NODE_TYPE]: FlowNode };
const edgeTypes = { [FLOW_EDGE_TYPE]: FlowEdge };

const FlowCanvas = () => {
  const {
    currentFlow,
    currentVersion,
    detailTarget,
    detailPanelOpen,
    toggleDetailPanel,
    setDetailTarget,
    showFailureNodes,
    toggleShowFailureNodes,
  } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    detailTarget: state.detailTarget,
    detailPanelOpen: state.detailPanelOpen,
    toggleDetailPanel: state.toggleDetailPanel,
    setDetailTarget: state.setDetailTarget,
    showFailureNodes: state.showFailureNodes,
    toggleShowFailureNodes: state.toggleShowFailureNodes,
  }));
  const { draftFlows, addTransition } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    addTransition: state.addTransition,
  }));
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [flowId, setFlowId] = useState<string>(currentFlow?._id.toString() ?? '');
  const { data: transitionData } = useOCPTransitionNumbers(flowId);

  const { data: emailCampaigns } = useQuery(
    ['campaign', currentFlow?._id],
    () => getCampaignsForOCPFlow(currentFlow?._id?.toString() ?? ''),
    { enabled: !!currentFlow },
  );

  const emailCampaignsData = React.useMemo(
    () =>
      emailCampaigns?.reduce((prevCampaigns, campaign) => {
        if (!campaign._id) {
          return prevCampaigns;
        }
        if (campaign.onboardingStepName) {
          prevCampaigns[campaign.onboardingStepName] = campaign;
        }
        if (campaign.onboardingSteps) {
          campaign.onboardingSteps.forEach(step => {
            prevCampaigns[step.name] = campaign;
          });
        }
        return prevCampaigns;
      }, {} as Record<string, EmailDripCampaign>) || {},
    [emailCampaigns],
  );

  useEffect(() => {
    if (currentFlow) {
      let flow: IOnboardingFlow | IOnboardingFlowDraft<string> = currentFlow;
      if (currentVersion === -1) flow = draftFlows[currentFlow._id];
      if (!flow) return;
      const { flowNodes, flowEdges } = dagreGraphLayout(
        flow.nodes,
        flow.edges,
        detailTarget as OnboardingFlowTransition,
        showFailureNodes,
        transitionData,
        emailCampaignsData,
      );
      setNodes(flowNodes as Node<any, string | undefined>[]);
      setEdges(flowEdges);
      setFlowId(currentFlow._id.toString());
    }
  }, [
    draftFlows,
    currentFlow,
    currentVersion,
    detailTarget,
    showFailureNodes,
    setNodes,
    setEdges,
    setFlowId,
    transitionData,
    emailCampaignsData,
  ]);

  const handleEdgeClick = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>, edge: Edge<any>) => {
      if (!detailPanelOpen) toggleDetailPanel();
      setDetailTarget(_.omit(edge.data, 'label') as unknown as OnboardingFlowTransition);
    },
    [detailPanelOpen, toggleDetailPanel, setDetailTarget],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (currentFlow && connection.source && connection.target)
        addTransition(currentFlow._id, connection.source ?? '', connection.target);
    },
    [addTransition, currentFlow],
  );

  const handlePaneClick = useCallback(() => {
    setDetailTarget(null);
    toggleDetailPanel();
  }, [setDetailTarget, toggleDetailPanel]);

  return (
    <>
      {currentFlow ? (
        <div
          style={{
            height: 'calc(100vh - 162px)',
            width: detailPanelOpen ? '68%' : '100%',
            backgroundColor: '#f0f0f0',
            position: 'relative',
          }}
        >
          <InfoBanner />
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onEdgeClick={handleEdgeClick}
            fitView
            edgesUpdatable={false}
            nodesDraggable={false}
            onConnect={handleConnect}
            onPaneClick={handlePaneClick}
          >
            <MiniMap style={minimapStyle} />
            <Controls />
            <Background color="#aaa" gap={16} />
          </ReactFlow>
          <div className="absolute bottom-24 left-4">
            <Switch
              label="Show Failure Terminuses"
              labelPlacement="right"
              checked={showFailureNodes}
              onCheckedChange={toggleShowFailureNodes}
              size="small"
            />
          </div>
        </div>
      ) : (
        <div className="p-2">Flow not found</div>
      )}
    </>
  );
};

export default FlowCanvas;
